import { useEffect, useRef, useState, useCallback } from "react";
import { useTrackEngagementMutation } from "../Slices/roomNateApis";

const INACTIVITY_LIMIT = 2 * 60 * 1000;

const useUptimeTracker = () => {
  const [postTrackEngagement] = useTrackEngagementMutation();
  const [startTime, setStartTime] = useState(null);
  const [totalUptime, setTotalUptime] = useState(0);
  const [isTracking, setIsTracking] = useState(false);
  
  const layoutIdRef = useRef(null);
  const versionIdRef = useRef(null);
  const isInactive = useRef(false);
  const isHidden = useRef(document.hidden);
  const inactivityTimeout = useRef(null);
  const startTimeRef = useRef(null);
  const isTrackingRef = useRef(isTracking);


  useEffect(() => {
    startTimeRef.current = startTime;
  }, [startTime]);

  useEffect(() => {
    isTrackingRef.current = isTracking;
  }, [isTracking]);

  const trackEngagement = useCallback((data) => {
    if (process.env.NODE_ENV === 'development') {
      return;
    }
    if (!layoutIdRef.current || !versionIdRef.current) return;
    
    postTrackEngagement({
      layoutId: layoutIdRef.current,
      versionId: versionIdRef.current,
      body: data,
    });
  }, [postTrackEngagement]);

  const logAndResetUptime = useCallback((label = "default") => {
    const endTime = Date.now();
    const start = startTimeRef.current;
    
    if (!start) return;
    
    const duration = endTime - start;
    if (duration <= 0) return;

    
    trackEngagement({
      uptime: {
        start,
        end: endTime,
        source: label,
      },
    });

    setTotalUptime((prev) => prev + duration);
    setStartTime(null);
    setIsTracking(false);
  }, [trackEngagement]);

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimeout.current) {
      clearTimeout(inactivityTimeout.current);
    }

    if (isInactive.current) {
      isInactive.current = false;
      const now = Date.now();
      setStartTime(now);
      startTimeRef.current = now;
    }

    inactivityTimeout.current = setTimeout(() => {
      if (!isInactive.current && startTimeRef.current) {
        logAndResetUptime("inactivity");
        isInactive.current = true;
      }
    }, INACTIVITY_LIMIT);
  }, [logAndResetUptime]);

  const handleVisibilityChange = useCallback(() => {
    const hidden = document.hidden;
    
    if (hidden) {
      if (!isHidden.current && startTimeRef.current) {
        logAndResetUptime("visibility");
        isHidden.current = true;
      }
    } else {
      if (isHidden.current) {
        const now = Date.now();
        setStartTime(now);
        startTimeRef.current = now;
        isHidden.current = false;
        setIsTracking(true);
      }
    }
  }, [logAndResetUptime]);

  const startTracking = useCallback(({layoutData, versionId}) => {

    if (!layoutData || versionId === undefined) return;


    
    layoutIdRef.current = layoutData._id;
    versionIdRef.current = versionId;
    
    const now = Date.now();
    setStartTime(now);
    startTimeRef.current = now;
    setIsTracking(true);
    
    isInactive.current = false;
    isHidden.current = document.hidden;
  }, []);

  useEffect(() => {
    const events = [
      "mousemove",
      "keydown", 
      "click",
      "touchstart",
      "touchmove",
      "touchend",
      "scroll",
    ];

    events.forEach((e) => window.addEventListener(e, resetInactivityTimer));
    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    resetInactivityTimer();

    return () => {

      events.forEach((e) =>
        window.removeEventListener(e, resetInactivityTimer)
      );
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      
      if (inactivityTimeout.current) {
        clearTimeout(inactivityTimeout.current);
      }
    };
  }, [resetInactivityTimer, handleVisibilityChange, postTrackEngagement]);

  useEffect(() => {
    return () => {

      if (startTimeRef.current && isTrackingRef.current) {
        const endTime = Date.now();
        const duration = endTime - startTimeRef.current;
        
        if (duration > 0) {
          
          if (process.env.NODE_ENV !== 'development' && layoutIdRef.current && versionIdRef.current) {
            postTrackEngagement({
              layoutId: layoutIdRef.current,
              versionId: versionIdRef.current,
              body: {
                uptime: {
                  start: startTimeRef.current,
                  end: endTime,
                  source: "unmount",
                },
              },
            });
          }
        }
      }
    }

  }, [postTrackEngagement]);
  

  useEffect(() => {
    if (!isTracking) return;

      const interval = setInterval(() => {
        if (startTimeRef.current && layoutIdRef.current && versionIdRef.current) {
          const now = Date.now();
          const duration = now - startTimeRef.current;
          if (duration > 0) {
            if (process.env.NODE_ENV !== 'development') {
              postTrackEngagement({
                layoutId: layoutIdRef.current,
                versionId: versionIdRef.current,
                body: {
                  uptime: {
                    start: startTimeRef.current,
                    end: now,
                    source: "interval",
                  },
                },
              });
            }
            setTotalUptime((prev) => prev + duration);
            setStartTime(now);
            startTimeRef.current = now;

          }
        }
      }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [isTracking, postTrackEngagement]);

  return { startTracking, totalUptime, isTracking };
};

export default useUptimeTracker;